import SkillCardTitle from './SkillCardTitle';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowUpTrayIcon,
  EllipsisHorizontalIcon,
  FolderOpenIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  TagIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { i18nService } from '../../services/i18n';
import { archiveSkillFolder, skillService } from '../../services/skill';
import { setSkills } from '../../store/slices/skillSlice';
import { RootState } from '../../store';
import { Skill } from '../../types/skill';
import { runSkillImportBatch, type SkillImportArchive, type VisibleSkillImportProgress } from '../../services/skillImportBatch';
import ErrorMessage from '../ErrorMessage';
import Tooltip from '../ui/Tooltip';
import SkillAvatar from './SkillAvatar';
import SkillDetailDialog from './SkillDetailDialog';

interface SkillsManagerProps {
  personalOnly?: boolean;
}

const importText = (key: string, values: Record<string, string | number> = {}) => {
  let value = i18nService.t(key);
  for (const [name, replacement] of Object.entries(values)) value = value.split(`{${name}}`).join(String(replacement));
  return value;
};

const getSkillTags = (skill: Skill): string[] => skill.tags?.length ? skill.tags : skill.category ? [skill.category] : [];

const SkillsManager: React.FC<SkillsManagerProps> = ({ personalOnly }) => {
  const dispatch = useDispatch();
  const allReduxSkills = useSelector((state: RootState) => state.skill.skills);
  // personalOnly 模式下只显示非内置的用户技能
  const skills = useMemo(() => {
    if (!personalOnly) return allReduxSkills;
    return allReduxSkills.filter(s => !s.isBuiltIn);
  }, [allReduxSkills, personalOnly]);

  const [skillSearchQuery, setSkillSearchQuery] = useState('');
  const [skillDownloadSource, setSkillDownloadSource] = useState('');
  const [skillActionError, setSkillActionError] = useState('');
  const [isDownloadingSkill, setIsDownloadingSkill] = useState(false);
  const [isAddSkillMenuOpen, setIsAddSkillMenuOpen] = useState(false);
  const [isGithubImportOpen, setIsGithubImportOpen] = useState(false);
  const [skillPendingDelete, setSkillPendingDelete] = useState<Skill | null>(null);
  const [isDeletingSkill, setIsDeletingSkill] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [tagEditingSkillId, setTagEditingSkillId] = useState<string | null>(null);
  const [tagDraft, setTagDraft] = useState<string[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [isSavingSkillTags, setIsSavingSkillTags] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState<string | null>(null);
  const [moreMenuSkillId, setMoreMenuSkillId] = useState<string | null>(null);
  const [language, setLanguage] = useState(i18nService.getLanguage());
  const [importProgress, setImportProgress] = useState<VisibleSkillImportProgress | null>(null);
  const [importResult, setImportResult] = useState<{ kind: 'folder' | 'batch'; installed: number; skipped: number; failed: number } | null>(null);
  const [pendingOverwrite, setPendingOverwrite] = useState<string[] | null>(null);

  const addSkillMenuRef = useRef<HTMLDivElement>(null);
  const addSkillButtonRef = useRef<HTMLButtonElement>(null);
  const githubImportInputRef = useRef<HTMLInputElement>(null);
  const tagEditorTriggerRef = useRef<HTMLButtonElement | null>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const zipInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const overwriteResolverRef = useRef<((overwrite: boolean) => void) | null>(null);

  const activeImport = useRef<AbortController | null>(null);

  useEffect(() => {
    /** 换账号/卸载中止批次；已送达服务器的包仍只归原用户，不误报撤销。 */
    const cancel = () => {
      activeImport.current?.abort(); activeImport.current = null;
      overwriteResolverRef.current?.(false); overwriteResolverRef.current = null;
      setPendingOverwrite(null); setImportProgress(null); setImportResult(null);
      setIsDownloadingSkill(false); setSkillActionError('');
    };
    window.addEventListener('auth:identityChanging', cancel);
    return () => { window.removeEventListener('auth:identityChanging', cancel); cancel(); };
  }, []);

  useEffect(() => {
    if (!pendingOverwrite) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        overwriteResolverRef.current?.(false);
        overwriteResolverRef.current = null;
        setPendingOverwrite(null);
      }
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [pendingOverwrite]);

  useEffect(() => {
    let isActive = true;
    const loadSkills = async () => {
      const loadedSkills = await skillService.loadSkills();
      if (!isActive) return;
      dispatch(setSkills(loadedSkills));
    };
    loadSkills();

    const unsubscribe = skillService.onSkillsChanged(async () => {
      const loadedSkills = await skillService.loadSkills();
      if (!isActive) return;
      dispatch(setSkills(loadedSkills));
    });

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [dispatch]);

  useEffect(() => i18nService.subscribe(() => setLanguage(i18nService.getLanguage())), []);

  useEffect(() => {
    if (!isAddSkillMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideMenu = addSkillMenuRef.current?.contains(target);
      const isInsideButton = addSkillButtonRef.current?.contains(target);
      if (!isInsideMenu && !isInsideButton) {
        setIsAddSkillMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsAddSkillMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isAddSkillMenuOpen]);

  useEffect(() => {
    if (!isGithubImportOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsGithubImportOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    setTimeout(() => githubImportInputRef.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isGithubImportOpen]);

  const predefinedCategories = useMemo(() => [
    '文档处理', '搜索资讯', '设计创作', '效率工具', '写作助手', '开发集成',
  ], []);

  useEffect(() => {
    if (!tagEditingSkillId || isSavingSkillTags) return;
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setTagEditingSkillId(null);
        setSkillActionError('');
        tagEditorTriggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [tagEditingSkillId, isSavingSkillTags]);

  useEffect(() => {
    if (!moreMenuSkillId) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        setMoreMenuSkillId(null);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMoreMenuSkillId(null);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [moreMenuSkillId]);

  const categoryLabelMap: Record<string, string> = useMemo(() => ({
    '文档处理': i18nService.t('categoryDocuments'),
    '搜索资讯': i18nService.t('categorySearch'),
    '设计创作': i18nService.t('categoryDesign'),
    '效率工具': i18nService.t('categoryTools'),
    '写作助手': i18nService.t('categoryWriting'),
    '开发集成': i18nService.t('categoryDev'),
    '通讯协作': i18nService.t('categoryCollaboration'),
    '开发': i18nService.t('categoryDev'),
    '数据分析': i18nService.t('categoryAnalysis'),
  }), [language]);

  const categories = useMemo(() => {
    const cats = new Set<string>();
    skills.forEach(skill => getSkillTags(skill).forEach(tag => cats.add(tag)));
    return Array.from(cats);
  }, [skills]);

  const tagEditorTags = useMemo(() => Array.from(new Set([...predefinedCategories, ...categories, ...tagDraft])), [predefinedCategories, categories, tagDraft]);

  const filteredSkills = useMemo(() => {
    const query = skillSearchQuery.toLowerCase();
    return skills.filter(skill => {
      const matchesSearch = skill.name.toLowerCase().includes(query)
        || skill.id.toLowerCase().includes(query)
        || skill.description.toLowerCase().includes(query)
        || getSkillTags(skill).some(tag => tag.toLowerCase().includes(query));
      const matchesCategory = selectedCategory === 'all'
        || (selectedCategory === 'uncategorized' && getSkillTags(skill).length === 0)
        || getSkillTags(skill).includes(selectedCategory);
      return matchesSearch && matchesCategory;
    });
  }, [skills, skillSearchQuery, selectedCategory]);

  const formatSkillDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const currentLang = i18nService.getLanguage();
    const locale = currentLang === 'zh' ? 'zh-CN' : currentLang === 'zh-TW' ? 'zh-TW' : 'en-US';
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date);
  };

  const handleToggleSkill = async (skillId: string) => {
    const targetSkill = skills.find(skill => skill.id === skillId);
    if (!targetSkill) return;
    try {
      const updatedSkills = await skillService.setSkillEnabled(skillId, !targetSkill.enabled);
      dispatch(setSkills(updatedSkills));
      setSkillActionError('');
    } catch (error) {
      setSkillActionError(error instanceof Error ? error.message : i18nService.t('skillUpdateFailed'));
    }
  };

  const beginEditTags = (skill: Skill) => {
    setTagEditingSkillId(skill.id);
    setTagDraft(getSkillTags(skill));
    setNewTagName('');
    setSkillActionError('');
  };

  const toggleDraftTag = (tag: string) => {
    setTagDraft(current => current.includes(tag)
      ? current.filter(item => item !== tag)
      : current.length < 12 ? [...current, tag] : current);
  };

  const addCustomTag = () => {
    const tag = newTagName.normalize('NFKC').trim().replace(/\s+/gu, ' ').slice(0, 30);
    if (!tag) return;
    setTagDraft(current => current.length >= 12 || current.some(item => item.toLocaleLowerCase('en-US') === tag.toLocaleLowerCase('en-US'))
      ? current : [...current, tag]);
    setNewTagName('');
  };

  const saveSkillTags = async () => {
    if (!tagEditingSkillId || isSavingSkillTags) return;
    setIsSavingSkillTags(true);
    try {
      const updated = await skillService.setSkillTags(tagEditingSkillId, tagDraft);
      dispatch(setSkills(updated));
      setTagEditingSkillId(null);
      setSkillActionError('');
      tagEditorTriggerRef.current?.focus();
    } catch (error) {
      setSkillActionError(error instanceof Error ? error.message : i18nService.t('skillUpdateFailed'));
    } finally {
      setIsSavingSkillTags(false);
    }
  };

  const handleRequestDeleteSkill = (skill: Skill) => {
    if (!skill.canManage) {
      setSkillActionError(i18nService.t('skillBuiltInCannotDelete'));
      return;
    }
    setSkillActionError('');
    setSkillPendingDelete(skill);
  };

  const handleCancelDeleteSkill = () => {
    if (isDeletingSkill) return;
    setSkillPendingDelete(null);
  };

  const handleConfirmDeleteSkill = async () => {
    if (!skillPendingDelete || isDeletingSkill) return;
    setIsDeletingSkill(true);
    setSkillActionError('');
    const result = await skillService.deleteSkill(skillPendingDelete.id);
    if (!result.success) {
      setSkillActionError(result.error || i18nService.t('skillDeleteFailed'));
      setIsDeletingSkill(false);
      return;
    }
    if (!Array.isArray(result.skills) || result.skills.some(skill => skill.id === skillPendingDelete.id)) {
      setSkillActionError(i18nService.t('skillDeleteFailed'));
      setIsDeletingSkill(false);
      return;
    }
    dispatch(setSkills(result.skills));
    setIsDeletingSkill(false);
    setSkillPendingDelete(null);
  };

  const handleAddSkillFromSource = async (source: string) => {
    const trimmedSource = source.trim();
    if (!trimmedSource) return;
    setIsDownloadingSkill(true);
    setSkillActionError('');
    const result = await skillService.downloadSkill(trimmedSource);
    setIsDownloadingSkill(false);
    if (!result.success) {
      setSkillActionError(result.error || i18nService.t('skillDownloadFailed'));
      return;
    }
    if (result.skills) {
      dispatch(setSkills(result.skills));
    }
    setSkillDownloadSource('');
    setIsAddSkillMenuOpen(false);
    setIsGithubImportOpen(false);
  };

  const resolveOverwrite = (overwrite: boolean) => {
    overwriteResolverRef.current?.(overwrite);
    overwriteResolverRef.current = null;
    setPendingOverwrite(null);
  };

  const requestOverwrite = (names: string[]) => new Promise<boolean>(resolve => {
    overwriteResolverRef.current = resolve;
    setPendingOverwrite(names);
  });

  /** 页面只管理交互生命周期，去重/失败隔离由批次服务负责。 */
  const importArchives = async (
    source: SkillImportArchive[] | (() => Promise<SkillImportArchive[]>),
    kind: 'folder' | 'batch',
  ) => {
    if (activeImport.current || isDownloadingSkill) return;
    const operation = new AbortController(); activeImport.current = operation;
    setIsDownloadingSkill(true); setSkillActionError(''); setImportResult(null);
    try {
      const archives = typeof source === 'function' ? await source() : source;
      operation.signal.throwIfAborted();
      const result = await runSkillImportBatch({
        archives, signal: operation.signal,
        upload: (...args) => skillService.importLocalSkill(...args),
        confirmOverwrite: requestOverwrite, onProgress: setImportProgress,
        onSkills: values => dispatch(setSkills(values)),
      });
      operation.signal.throwIfAborted();
      setImportResult({ kind, installed: result.installed, skipped: result.skipped, failed: result.failed });
      if (result.errors.length) setSkillActionError(result.errors.join('；'));
    } catch (error) {
      if (operation.signal.aborted) return;
      setImportResult({ kind, installed: 0, skipped: 0, failed: 1 });
      const code = error instanceof Error ? error.message : '';
      setSkillActionError(i18nService.t(code === 'UNSAFE_SOURCE' ? 'skillImportUnsafeSource'
        : code === 'PACKAGE_TOO_LARGE' ? 'skillImportPackageTooLarge' : 'skillImportFailed'));
    } finally {
      if (activeImport.current === operation) {
        activeImport.current = null; setImportProgress(null); setIsDownloadingSkill(false);
      }
    }
  };

  const handleUploadSkillZip = () => {
    if (isDownloadingSkill) return;
    setIsAddSkillMenuOpen(false);
    zipInputRef.current?.click();
  };

  const handleUploadSkillFolder = () => {
    if (isDownloadingSkill) return;
    setIsAddSkillMenuOpen(false);
    folderInputRef.current?.click();
  };

  const handleImportFromGithub = async () => {
    if (isDownloadingSkill) return;
    await handleAddSkillFromSource(skillDownloadSource);
  };

  return (
    <div className="space-y-4">
      <input
        ref={zipInputRef}
        type="file"
        accept=".zip,application/zip"
        multiple
        className="hidden"
        onChange={event => {
          const files = Array.from(event.currentTarget.files || []);
          event.currentTarget.value = '';
          void importArchives(files.map(file => ({ name: file.name, blob: file })), 'batch');
        }}
      />
      <input
        ref={folderInputRef}
        type="file"
        multiple
        className="hidden"
        {...{ webkitdirectory: '' }}
        onChange={async event => {
          const files = Array.from(event.currentTarget.files || []);
          event.currentTarget.value = '';
          if (files.length === 0) return;
          const folderName = files[0].webkitRelativePath.split('/')[0] || i18nService.t('uploadSkillFolder');
          void importArchives(async () => {
            setImportProgress({ sourceLabel: folderName, phase: 'packaging', processed: 0, installed: 0 });
            return [{ name: folderName, blob: await archiveSkillFolder(files) }];
          }, 'folder');
        }}
      />
      <div className="flex items-center justify-between gap-4">
        <p className="text-[13px] leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">
          {i18nService.t('skillsDescription')}
        </p>
        <span className="shrink-0 text-xs tabular-nums text-claude-textSecondary/75 dark:text-claude-darkTextSecondary/75">
          {filteredSkills.length} / {skills.length}
        </span>
      </div>

      {skillActionError && !tagEditingSkillId && (
        <ErrorMessage
          message={skillActionError}
          onClose={() => setSkillActionError('')}
        />
      )}
      {importResult && (
        <div role="status" className={`rounded-md px-3 py-2 text-sm ${importResult.failed ? 'bg-amber-500/10 text-amber-800 dark:text-amber-300' : 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300'}`}>
          {importText(importResult.kind === 'folder' ? 'skillFolderUploadResult' : 'skillBatchUploadResult', importResult)}
        </div>
      )}
      {importProgress && (
        <div role="status" aria-live="polite" className="rounded-md bg-blue-500/10 px-3 py-2 text-sm text-blue-800 dark:text-blue-300">
          {importText(`skillImport${importProgress.phase[0].toUpperCase()}${importProgress.phase.slice(1)}`, {
            source: importProgress.sourceLabel,
            processed: importProgress.processed,
            total: importProgress.total ?? 0,
            installed: importProgress.installed,
          })}
          {importProgress.total !== undefined && importProgress.total > 0 && (
            <div role="progressbar" aria-label={i18nService.t('skillImportProgressLabel')} aria-valuemin={0} aria-valuemax={importProgress.total} aria-valuenow={importProgress.processed} className="mt-2 h-1.5 overflow-hidden rounded-full bg-blue-500/20">
              <div className="h-full bg-blue-600 transition-[width] duration-200" style={{ width: `${Math.min(100, 100 * importProgress.processed / importProgress.total)}%` }} />
            </div>
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
          <input
            type="text"
            placeholder={i18nService.t('searchSkills')}
            value={skillSearchQuery}
            onChange={(e) => setSkillSearchQuery(e.target.value)}
            className="h-9 w-full rounded-lg border border-claude-border bg-white pl-9 pr-3 text-[13px] text-claude-text shadow-[0_1px_2px_rgba(0,0,0,0.02)] outline-none transition-[border-color,box-shadow] placeholder:text-claude-textSecondary/70 focus:border-claude-accent/60 focus:ring-4 focus:ring-claude-accent/10 dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkText dark:placeholder:text-claude-darkTextSecondary/65"
          />
        </div>
        <div className="relative">
          <button
            ref={addSkillButtonRef}
            type="button"
            onClick={() => setIsAddSkillMenuOpen(prev => !prev)}
            className="flex h-9 items-center gap-2 rounded-lg border border-claude-border bg-white px-3 text-[13px] font-medium text-claude-text transition-colors hover:bg-claude-surfaceHover dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:text-claude-darkText dark:hover:bg-claude-darkSurfaceHover"
          >
            <PlusCircleIcon className="h-4 w-4" />
            <span>{i18nService.t('addSkill')}</span>
          </button>

          {isAddSkillMenuOpen && (
            <div
              ref={addSkillMenuRef}
              className="absolute right-0 mt-2 w-72 rounded-xl border dark:border-claude-darkBorder border-claude-border dark:bg-claude-darkSurface bg-claude-surface shadow-lg z-50 overflow-hidden"
            >
              <button
                type="button"
                onClick={handleUploadSkillZip}
                disabled={isDownloadingSkill}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors disabled:opacity-50"
              >
                <ArrowUpTrayIcon className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
                <span>{i18nService.t('uploadSkillZip')}</span>
              </button>
              <button
                type="button"
                onClick={handleUploadSkillFolder}
                disabled={isDownloadingSkill}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-sm dark:text-claude-darkText text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors disabled:opacity-50"
              >
                <FolderOpenIcon className="h-4 w-4 dark:text-claude-darkTextSecondary text-claude-textSecondary" />
                <span>{i18nService.t('uploadSkillFolder')}</span>
              </button>
              {/* 从 GitHub 导入按钮暂时隐藏 */}
            </div>
          )}
        </div>
      </div>

      {categories.length > 0 && (
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          <button
            type="button"
            onClick={() => setSelectedCategory('all')}
            className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-claude-text text-white dark:bg-claude-darkText dark:text-claude-darkBg'
                : 'text-claude-textSecondary hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText'
            }`}
          >
            {i18nService.t('allCategories')}
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                selectedCategory === cat
                  ? 'bg-claude-text text-white dark:bg-claude-darkText dark:text-claude-darkBg'
                  : 'text-claude-textSecondary hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText'
              }`}
            >
              {categoryLabelMap[cat] || cat}
            </button>
          ))}
          {skills.some(s => getSkillTags(s).length === 0) && (
            <button
              type="button"
              onClick={() => setSelectedCategory('uncategorized')}
              className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                selectedCategory === 'uncategorized'
                  ? 'bg-claude-text text-white dark:bg-claude-darkText dark:text-claude-darkBg'
                  : 'text-claude-textSecondary hover:bg-claude-surfaceHover hover:text-claude-text dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText'
              }`}
            >
              {i18nService.t('uncategorized')}
            </button>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
        {filteredSkills.length === 0 ? (
          <div className="col-span-full py-8 text-center text-sm text-claude-textSecondary dark:text-claude-darkTextSecondary">
            {i18nService.t('noSkillsAvailable')}
          </div>
        ) : (
          filteredSkills.map((skill) => (
            <div
              key={skill.id}
              role="button"
              tabIndex={0}
              aria-label={`${i18nService.t('skillDetailsOpen')} · ${skill.name}`}
              onClick={() => setSelectedSkillId(skill.id)}
              onKeyDown={(event) => {
                if (event.target !== event.currentTarget) return;
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setSelectedSkillId(skill.id);
                }
              }}
              className={`group relative flex min-h-[120px] cursor-pointer flex-col rounded-xl border border-claude-border bg-white p-3 shadow-[0_1px_2px_rgba(0,0,0,0.02)] outline-none transition-all duration-200 hover:border-claude-textSecondary/30 hover:shadow-[0_8px_24px_rgba(15,23,42,0.05)] focus-visible:border-claude-accent/60 focus-visible:ring-2 focus-visible:ring-claude-accent/20 dark:border-claude-darkBorder dark:bg-claude-darkSurface dark:hover:border-claude-darkTextSecondary/40 ${moreMenuSkillId === skill.id ? 'z-20' : ''}`}
            >
              <div className="mb-2 flex items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <SkillAvatar name={skill.name} className="h-8 w-8 rounded-lg [&_svg]:h-3.5 [&_svg]:w-3.5" />
                  <SkillCardTitle name={skill.name} identifier={skill.id} />
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {skill.canManage && (
                    <div className="relative" ref={moreMenuSkillId === skill.id ? moreMenuRef : undefined}>
                      <button
                        type="button"
                        aria-label={`${i18nService.t('coworkSessionActions')}: ${skill.name}`}
                        aria-haspopup="menu"
                        aria-expanded={moreMenuSkillId === skill.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          tagEditorTriggerRef.current = e.currentTarget;
                          setMoreMenuSkillId(prev => prev === skill.id ? null : skill.id);
                        }}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-claude-textSecondary transition-colors hover:bg-claude-surfaceHover hover:text-claude-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-claude-accent/60 dark:text-claude-darkTextSecondary dark:hover:bg-claude-darkSurfaceHover dark:hover:text-claude-darkText"
                      >
                        <EllipsisHorizontalIcon className="h-4 w-4" />
                      </button>
                      {moreMenuSkillId === skill.id && (
                        <div role="menu" className="absolute right-0 top-full z-50 mt-1 w-36 rounded-md border border-claude-border bg-claude-surface py-1 shadow-lg dark:border-claude-darkBorder dark:bg-claude-darkSurface">
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMoreMenuSkillId(null);
                              beginEditTags(skill);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-claude-text transition-colors hover:bg-claude-surfaceHover dark:text-claude-darkText dark:hover:bg-claude-darkSurfaceHover"
                          >
                            <PencilSquareIcon className="h-3.5 w-3.5" />
                            <span>{i18nService.t('skillEditTags')}</span>
                          </button>
                          <button
                            type="button"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMoreMenuSkillId(null);
                              handleRequestDeleteSkill(skill);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-red-500 transition-colors hover:bg-claude-surfaceHover dark:text-red-400 dark:hover:bg-claude-darkSurfaceHover"
                          >
                            <TrashIcon className="h-3.5 w-3.5" />
                            <span>{i18nService.t('deleteSkill')}</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  <div
                    className={`flex h-[18px] w-8 flex-shrink-0 cursor-pointer items-center rounded-full transition-colors ${
                      skill.enabled ? 'bg-claude-accent' : 'dark:bg-claude-darkBorder bg-claude-border'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleToggleSkill(skill.id);
                    }}
                  >
                    <div
                      className={`h-3 w-3 rounded-full bg-white shadow-md transform transition-transform ${
                        skill.enabled ? 'translate-x-[17px]' : 'translate-x-[3px]'
                      }`}
                    />
                  </div>
                </div>
              </div>

              <Tooltip
                content={skill.description}
                position="bottom"
                maxWidth="360px"
                className="block w-full"
              >
                <p className="mb-2 line-clamp-2 text-xs leading-[18px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                  {skill.description}
                </p>
              </Tooltip>

              <div className="mt-auto flex items-center justify-between text-[10px] text-claude-textSecondary dark:text-claude-darkTextSecondary">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="shrink-0">{formatSkillDate(skill.updatedAt)}</span>
                  {getSkillTags(skill).length > 0 && (
                    <div className="flex min-w-0 items-center gap-1 overflow-hidden">
                      <TagIcon className="h-3 w-3 shrink-0" />
                      {getSkillTags(skill).slice(0, 2).map(tag => (
                        <span key={tag} className="max-w-20 truncate rounded bg-claude-surfaceMuted px-1 py-0.5 dark:bg-claude-darkSurfaceHover" title={tag}>
                          {categoryLabelMap[tag] || tag}
                        </span>
                      ))}
                      {getSkillTags(skill).length > 2 && <span>+{getSkillTags(skill).length - 2}</span>}
                    </div>
                  )}
                </div>

              </div>
            </div>
          ))
        )}
      </div>

      {tagEditingSkillId && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !isSavingSkillTags) {
              setTagEditingSkillId(null);
              setSkillActionError('');
              tagEditorTriggerRef.current?.focus();
            }
          }}
        >
          <div role="dialog" aria-modal="true" aria-labelledby="skill-tags-title" className="w-full max-w-md rounded-md border border-claude-border bg-white p-5 shadow-2xl dark:border-claude-darkBorder dark:bg-claude-darkSurface">
            <h2 id="skill-tags-title" className="text-base font-semibold text-claude-text dark:text-claude-darkText">{i18nService.t('skillEditTags')}</h2>
            <div className="mt-4 flex max-h-44 flex-wrap gap-2 overflow-y-auto">
              {tagEditorTags.map(tag => (
                <button
                  key={tag}
                  type="button"
                  aria-pressed={tagDraft.includes(tag)}
                  disabled={isSavingSkillTags || (!tagDraft.includes(tag) && tagDraft.length >= 12)}
                  onClick={() => toggleDraftTag(tag)}
                  className={`rounded-md border px-2.5 py-1.5 text-xs transition-colors disabled:opacity-50 ${tagDraft.includes(tag)
                    ? 'border-claude-accent bg-claude-accent text-white'
                    : 'border-claude-border text-claude-text hover:border-claude-accent/50 dark:border-claude-darkBorder dark:text-claude-darkText'}`}
                >
                  {categoryLabelMap[tag] || tag}
                </button>
              ))}
            </div>
            <div className="mt-4 flex gap-2">
              <input
                autoFocus
                value={newTagName}
                onChange={event => setNewTagName(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addCustomTag();
                  }
                }}
                disabled={isSavingSkillTags || tagDraft.length >= 12}
                maxLength={30}
                placeholder={i18nService.t('skillNewTagPlaceholder')}
                className="min-w-0 flex-1 rounded-md border border-claude-border bg-transparent px-3 py-2 text-sm text-claude-text outline-none focus:ring-1 focus:ring-claude-accent dark:border-claude-darkBorder dark:text-claude-darkText"
              />
              <button type="button" onClick={addCustomTag} disabled={isSavingSkillTags || tagDraft.length >= 12 || !newTagName.trim()} className="rounded-md border border-claude-border px-3 text-sm text-claude-text disabled:opacity-50 dark:border-claude-darkBorder dark:text-claude-darkText">
                {i18nService.t('skillAddTag')}
              </button>
            </div>
            {skillActionError && <p role="alert" className="mt-3 text-sm text-red-500">{skillActionError}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" disabled={isSavingSkillTags} onClick={() => { setTagEditingSkillId(null); setSkillActionError(''); tagEditorTriggerRef.current?.focus(); }} className="rounded-md border border-claude-border px-3 py-1.5 text-sm text-claude-text disabled:opacity-50 dark:border-claude-darkBorder dark:text-claude-darkText">{i18nService.t('cancel')}</button>
              <button type="button" disabled={isSavingSkillTags} onClick={saveSkillTags} className="rounded-md bg-claude-accent px-3 py-1.5 text-sm text-white disabled:opacity-50">{i18nService.t('save')}</button>
            </div>
          </div>
        </div>
      )}

      {skillPendingDelete && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={handleCancelDeleteSkill}
        >
          <div
            className="w-full max-w-sm mx-4 rounded-2xl dark:bg-claude-darkSurface bg-claude-surface border dark:border-claude-darkBorder border-claude-border shadow-2xl p-5"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
              {i18nService.t('deleteSkill')}
            </div>
            <p className="mt-2 text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
              {i18nService.t('skillDeleteConfirm').replace('{name}', skillPendingDelete.name)}
            </p>
            {skillActionError && (
              <div className="mt-3 text-xs text-red-500">
                {skillActionError}
              </div>
            )}
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelDeleteSkill}
                disabled={isDeletingSkill}
                className="px-3 py-1.5 text-xs rounded-lg border dark:border-claude-darkBorder border-claude-border dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {i18nService.t('cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteSkill}
                disabled={isDeletingSkill}
                className="px-3 py-1.5 text-xs rounded-lg bg-red-500 text-white hover:bg-red-600 dark:bg-red-500 dark:hover:bg-red-400 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {i18nService.t('confirmDelete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingOverwrite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" role="presentation">
          <div role="alertdialog" aria-modal="true" aria-labelledby="skill-overwrite-title" className="mx-4 w-full max-w-sm rounded-md border border-claude-border bg-white p-5 shadow-2xl dark:border-claude-darkBorder dark:bg-claude-darkSurface">
            <h3 id="skill-overwrite-title" className="text-base font-semibold text-claude-text dark:text-claude-darkText">
              {i18nService.t('skillDuplicatesConfirm')}
            </h3>
            <ul className="mt-3 max-h-40 overflow-y-auto text-sm text-claude-textSecondary dark:text-claude-darkTextSecondary">
              {pendingOverwrite.map(name => <li key={name} className="py-0.5">{name}</li>)}
            </ul>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" autoFocus onClick={() => resolveOverwrite(false)} className="rounded-md border border-claude-border px-3 py-1.5 text-sm dark:border-claude-darkBorder dark:text-claude-darkText">
                {i18nService.t('skillSkipDuplicates')}
              </button>
              <button type="button" onClick={() => resolveOverwrite(true)} className="rounded-md bg-claude-accent px-3 py-1.5 text-sm text-white">
                {i18nService.t('skillOverwriteDuplicates')}
              </button>
            </div>
          </div>
        </div>
      )}

      {isGithubImportOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={() => setIsGithubImportOpen(false)}
        >
          <div
            className="w-full max-w-md mx-4 rounded-2xl dark:bg-claude-darkSurface bg-claude-surface border dark:border-claude-darkBorder border-claude-border shadow-2xl p-6"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-lg font-semibold dark:text-claude-darkText text-claude-text">
                  {i18nService.t('githubImportTitle')}
                </div>
                <p className="mt-1 text-sm dark:text-claude-darkTextSecondary text-claude-textSecondary">
                  {i18nService.t('githubImportDescription')}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsGithubImportOpen(false)}
                className="p-1.5 rounded-lg dark:text-claude-darkTextSecondary text-claude-textSecondary dark:hover:text-claude-darkText hover:text-claude-text dark:hover:bg-claude-darkSurfaceHover hover:bg-claude-surfaceHover transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-5 space-y-3">
              <div className="text-xs font-semibold tracking-wide dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {i18nService.t('githubImportUrlLabel')}
              </div>
              <input
                ref={githubImportInputRef}
                type="text"
                value={skillDownloadSource}
                onChange={(e) => setSkillDownloadSource(e.target.value)}
                placeholder={i18nService.t('githubSkillPlaceholder')}
                className="w-full px-3 py-2.5 text-sm rounded-xl dark:bg-claude-darkBg bg-claude-bg dark:text-claude-darkText text-claude-text dark:placeholder-claude-darkTextSecondary placeholder-claude-textSecondary border dark:border-claude-darkBorder border-claude-border focus:outline-none focus:ring-2 focus:ring-claude-accent"
              />
              <p className="text-xs dark:text-claude-darkTextSecondary text-claude-textSecondary">
                {i18nService.t('githubImportExamples')}
              </p>
              {skillActionError && (
                <div className="text-xs text-red-500">
                  {skillActionError}
                </div>
              )}
              <button
                type="button"
                onClick={handleImportFromGithub}
                disabled={isDownloadingSkill || !skillDownloadSource.trim()}
                className="w-full py-2.5 rounded-xl bg-claude-accent text-white text-sm font-medium hover:bg-claude-accent/90 transition-colors disabled:opacity-50"
              >
                {isDownloadingSkill ? i18nService.t('importingSkill') : i18nService.t('importSkill')}
              </button>
            </div>
          </div>
        </div>
      )}

      <SkillDetailDialog
        item={(() => {
          const selected = selectedSkillId ? skills.find(skill => skill.id === selectedSkillId) ?? null : null;
          if (!selected) return null;
          return {
            kind: 'local' as const,
            skill: selected,
            description: selected.description,
            categoryLabel: selected.category ? (categoryLabelMap[selected.category] || selected.category) : undefined,
            onToggle: handleToggleSkill,
          };
        })()}
        onClose={() => setSelectedSkillId(null)}
      />
    </div>
  );
};

export default SkillsManager;
